import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  GreetingType,
  MicrosoftTeamsCallQueue,
  MicrosoftTeamsCallQueueView,
  MusicOnHoldType,
  RoutingMethod,
} from '../../shared/models/generated/ziroModels';
import {
  OfferPromptType,
  UiAudioSettings,
  UiCallbackSettings,
} from './teams-call-queue-form/teams-call-queue-edit-form.component';
import {
  mapNoAgentSettingsViewToUi,
  mapOverflowSettingsViewToUi,
  mapTimeoutSettingsViewToUi,
  UiExceptionHandlingSettings,
} from './teams-call-queue-form/exception-handling-form/call-queue-exception-handling-form.component';
import { AudioFileResource } from '../../shared/resources/audio-file.resource';
import { TeamsCallQueuesContext } from '../teams-call-queues.context';
import { TeamsCallQueuesFieldConfigResource } from '../../helpdesk/shared/resources/teams-call-queues-field-config.resource';
import { TeamsCallQueueTeamsUsersSearchResource } from '../teams-call-queue-teams-users-search.resource';

export type ResourceAccountEntityArrayName = keyof Pick<
  MicrosoftTeamsCallQueue,
  'callingIdResourceAccountGuids' | 'resourceAccountGuids'
>;

export type ResourceAccountViewArrayName = keyof Pick<
  MicrosoftTeamsCallQueueView,
  'callingIdResourceAccounts' | 'resourceAccounts'
>;

export interface UiCallQueue extends MicrosoftTeamsCallQueue {
  audioSettings: UiAudioSettings;
  callbackSettings: UiCallbackSettings;
  uiOverflowSettings?: UiExceptionHandlingSettings;
  uiTimeoutSettings?: UiExceptionHandlingSettings;
  uiNoAgentSettings?: UiExceptionHandlingSettings;
}

export interface LanguageOption {
  label: string;
  value: string;
}

@Component({
  selector: 'ziro-teams-call-queue-edit',
  templateUrl: './teams-call-queue-edit.component.html',
  standalone: false,
})
export class TeamsCallQueueEditComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _audioFileResource = inject(AudioFileResource);
  private _teamsCallQueuesFieldConfigResource = inject(TeamsCallQueuesFieldConfigResource);
  private _msTeamsCallQueueTeamsUsersSearchResource = inject(TeamsCallQueueTeamsUsersSearchResource);
  private _teamsCallQueuesContext = inject(TeamsCallQueuesContext);

  isLoading = true;
  callQueueView: MicrosoftTeamsCallQueueView;
  callQueueEntity: UiCallQueue;
  invalidResourceAccountOrAgent = false;
  languageOptions: LanguageOption[] = [];
  is360ViewChild = false;

  private _callQueueId: string;

  ngOnInit(): void {
    const { tenantId, callQueueId, upn } = this._route.snapshot.params;
    this.is360ViewChild = this._route.snapshot.data['is360ViewChild'];
    this._callQueueId = this._route.snapshot.params['callQueueId'];

    combineLatest([
      this._teamsCallQueuesFieldConfigResource.get(tenantId),
      this.is360ViewChild
        ? this._msTeamsCallQueueTeamsUsersSearchResource.search(tenantId, upn, null, 250, 'RESOURCE_ACCOUNT')
        : of(null),
    ])
      .pipe(
        switchMap(([fieldConfig, resourceAccountArr]) => {
          const languageOptions: LanguageOption[] = (fieldConfig?.languages ?? []).map((lang) => ({
            label: lang.displayName,
            value: lang.code,
          }));

          this.languageOptions = languageOptions;

          const defaultLanguageCode = languageOptions.some((o) => o.value === 'en-US')
            ? 'en-US'
            : (languageOptions[0]?.value ?? '');

          if (!this._callQueueId) {
            this.callQueueView = {
              agentAlertTime: 15,
              allowOptOut: false,
              audioSettings: {
                greetingAudioId: '',
                greetingTtsMessage: '',
                greetingType: GreetingType.NONE,
                musicOnHoldAudioId: '',
                musicOnHoldType: MusicOnHoldType.NONE,
              },
              callQueueId: null,
              callingIdResourceAccounts: [],
              conferenceMode: true,
              groups: [],
              language: defaultLanguageCode,
              name: '',
              presenceAwareRouting: false,
              resourceAccounts: [],
              routingMethod: RoutingMethod.ATTENDANT,
              users: [],
              tenantId,
              microsoftTeamsTeams: null,
              teamSchedulingGroup: null,
              microsoftTeamsChannel: null,
              overflowSettings: null,
              timeoutSettings: null,
              noAgentSettings: null,
              callbackSettings: {
                enabled: false,
                requestDtmf: null,
                waitTimeBeforeOfferInSeconds: null,
                numberOfCallsInQueueBeforeOffer: null,
                callToAgentRatioThresholdBeforeOffer: null,
                offerAudioPromptFileGuid: '',
                offerTtsPrompt: '',
                notificationTargetGroup: null,
              },
            };

            const {
              agentAlertTime,
              allowOptOut,
              audioSettings,
              callQueueId,
              callingIdResourceAccounts,
              callbackSettings,
              conferenceMode,
              groups,
              language,
              name,
              presenceAwareRouting,
              resourceAccounts,
              routingMethod,
              users,
            } = this.callQueueView;

            this.callQueueEntity = {
              tenantId,
              agentAlertTime,
              allowOptOut,
              audioSettings,
              callQueueId,
              conferenceMode,
              language,
              name,
              presenceAwareRouting,
              routingMethod,
              callingIdResourceAccountGuids: callingIdResourceAccounts.map((ra) => ra.guid),
              resourceAccountGuids:
                !this.is360ViewChild && this._callQueueId
                  ? resourceAccounts.map((ra) => ra.guid)
                  : resourceAccountArr?.[0]?.guid
                    ? [resourceAccountArr?.[0]?.guid]
                    : [],
              groupGuids: groups.map((group) => group.guid),
              userGuids: users.map((user) => user.guid),
              teamSchedulingGroupId: '',
              microsoftTeamsTeamsId: '',
              microsoftTeamsChannelId: '',
              overflowSettings: null,
              timeoutSettings: null,
              noAgentSettings: null,
              callbackSettings: {
                enabled: callbackSettings.enabled,
                requestDtmf: callbackSettings.requestDtmf,
                waitTimeBeforeOfferInSeconds: callbackSettings.waitTimeBeforeOfferInSeconds,
                numberOfCallsInQueueBeforeOffer: callbackSettings.numberOfCallsInQueueBeforeOffer,
                callToAgentRatioThresholdBeforeOffer: callbackSettings.callToAgentRatioThresholdBeforeOffer,
                offerAudioPromptFileGuid: callbackSettings.offerAudioPromptFileGuid,
                offerTtsPrompt: callbackSettings.offerTtsPrompt,
                notificationTargetGroupGuid: '',
              },
              ...this._buildUiExceptionSettings(this.callQueueView),
            };

            return of(this.callQueueView);
          } else {
            return this._teamsCallQueuesContext.getCallQueue(callQueueId, tenantId).pipe(
              map((callQueue) => {
                this.callQueueView = callQueue;

                const {
                  agentAlertTime,
                  allowOptOut,
                  audioSettings,
                  callQueueId,
                  callingIdResourceAccounts,
                  callbackSettings,
                  conferenceMode,
                  groups,
                  language,
                  microsoftTeamsChannel,
                  microsoftTeamsTeams,
                  name,
                  presenceAwareRouting,
                  resourceAccounts,
                  routingMethod,
                  teamSchedulingGroup,
                  users,
                } = this.callQueueView;

                this.callQueueEntity = {
                  tenantId,
                  agentAlertTime,
                  allowOptOut,
                  audioSettings,
                  callQueueId,
                  conferenceMode,
                  language,
                  name,
                  presenceAwareRouting,
                  routingMethod,
                  callingIdResourceAccountGuids: callingIdResourceAccounts.map((ra) => ra.guid),
                  resourceAccountGuids: resourceAccounts.map((ra) => ra.guid),
                  groupGuids: groups.map((group) => group.guid),
                  userGuids: users.map((user) => user.guid),
                  teamSchedulingGroupId: teamSchedulingGroup ? teamSchedulingGroup.id : '',
                  microsoftTeamsTeamsId: microsoftTeamsTeams ? microsoftTeamsTeams.id : '',
                  microsoftTeamsChannelId: microsoftTeamsChannel ? microsoftTeamsChannel.id : '',
                  overflowSettings: null,
                  timeoutSettings: null,
                  noAgentSettings: null,
                  callbackSettings: {
                    enabled: callbackSettings.enabled,
                    requestDtmf: callbackSettings.requestDtmf,
                    waitTimeBeforeOfferInSeconds: callbackSettings.waitTimeBeforeOfferInSeconds,
                    numberOfCallsInQueueBeforeOffer: callbackSettings.numberOfCallsInQueueBeforeOffer,
                    callToAgentRatioThresholdBeforeOffer: callbackSettings.callToAgentRatioThresholdBeforeOffer,
                    offerAudioPromptFileGuid: callbackSettings.offerAudioPromptFileGuid,
                    offerTtsPrompt: callbackSettings.offerTtsPrompt,
                    notificationTargetGroupGuid: callbackSettings.notificationTargetGroup?.guid ?? '',
                    offerPromptType: callbackSettings.offerAudioPromptFileGuid
                      ? OfferPromptType.AUDIO_FILE
                      : callbackSettings.offerTtsPrompt
                        ? OfferPromptType.TTS_MESSAGE
                        : undefined,
                    notificationTargetGroup: callbackSettings.notificationTargetGroup?.guid
                      ? {
                          label: callbackSettings.notificationTargetGroup.displayName,
                          value: callbackSettings.notificationTargetGroup.guid,
                        }
                      : undefined,
                  },
                  ...this._buildUiExceptionSettings(this.callQueueView),
                };

                return this.callQueueView;
              }),
            );
          }
        }),
        switchMap((callQueueView) => {
          const {
            audioSettings: { greetingAudioId, musicOnHoldAudioId },
            callbackSettings,
          } = callQueueView;
          const offerAudioPromptFileGuid = callbackSettings?.offerAudioPromptFileGuid;

          const audioFetches: { id: string; target: 'greetingUpload' | 'musicOnHoldUpload' | 'offerAudioUpload' }[] =
            [];
          if (greetingAudioId) {
            audioFetches.push({ id: greetingAudioId, target: 'greetingUpload' });
          }
          if (musicOnHoldAudioId) {
            audioFetches.push({ id: musicOnHoldAudioId, target: 'musicOnHoldUpload' });
          }
          if (offerAudioPromptFileGuid) {
            audioFetches.push({ id: offerAudioPromptFileGuid, target: 'offerAudioUpload' });
          }

          if (!audioFetches.length) {
            return of(null);
          }

          return combineLatest(
            audioFetches.map((fetch) =>
              this._audioFileResource
                .getAudioFile(tenantId, 'call-queues', fetch.id)
                .pipe(map((file) => ({ file, target: fetch.target }))),
            ),
          ).pipe(
            tap((results) => {
              results.forEach(({ file, target }) => {
                if (target === 'offerAudioUpload') {
                  this.callQueueEntity = {
                    ...this.callQueueEntity,
                    callbackSettings: { ...this.callQueueEntity.callbackSettings, offerAudioUpload: file },
                  };
                } else {
                  this.callQueueEntity = {
                    ...this.callQueueEntity,
                    audioSettings: { ...this.callQueueEntity.audioSettings, [target]: file },
                  };
                }
              });
            }),
          );
        }),
        switchMap(() => this._fetchExceptionHandlingPromptAudio(tenantId)),
      )
      .subscribe(() => (this.isLoading = false));
  }

  setInvalidResourceAccountOrAgent(isQueueValid: boolean): void {
    this.invalidResourceAccountOrAgent = !isQueueValid;
  }

  private _buildUiExceptionSettings(
    callQueueView: MicrosoftTeamsCallQueueView,
  ): Pick<UiCallQueue, 'uiOverflowSettings' | 'uiTimeoutSettings' | 'uiNoAgentSettings'> {
    return {
      uiOverflowSettings: mapOverflowSettingsViewToUi(callQueueView.overflowSettings),
      uiTimeoutSettings: mapTimeoutSettingsViewToUi(callQueueView.timeoutSettings),
      uiNoAgentSettings: mapNoAgentSettingsViewToUi(callQueueView.noAgentSettings),
    };
  }

  private _fetchExceptionHandlingPromptAudio(tenantId: string): Observable<File[] | null> {
    const sectionsWithAudio = (['uiOverflowSettings', 'uiTimeoutSettings', 'uiNoAgentSettings'] as const).filter(
      (sectionKey) => this.callQueueEntity[sectionKey].audioPromptFileGuid,
    );

    if (!sectionsWithAudio.length) {
      return of(null);
    }

    return forkJoin(
      sectionsWithAudio.map((sectionKey) =>
        this._audioFileResource
          .getAudioFile(tenantId, 'call-queues', this.callQueueEntity[sectionKey].audioPromptFileGuid)
          .pipe(
            tap((audioFile) => {
              this.callQueueEntity = {
                ...this.callQueueEntity,
                [sectionKey]: {
                  ...this.callQueueEntity[sectionKey],
                  audioPromptUpload: audioFile,
                },
              };
            }),
          ),
      ),
    );
  }
}
